import React, { useState, useRef, useEffect } from 'react';
import {useDispatch, useSelector} from "react-redux";
import { RootState } from "../../../../redux/store";
import { Flex, Input, Button } from "antd";
import type { InputRef } from "antd";
// @ts-ignore
import { ReactComponent as LogoIcon } from '../../../../assets/images/logo/logo.svg';
import styles from './PhoneVerify.module.scss';
import CustomButton from "../../../../components/UI/CustomButton/CustomButton";
import {useNavigate} from "react-router";
import {
  useGetMeQuery,
  useSignUpMutation, useUpdateProfileMutation,
  useVerifyCodeMutation,
  useVerifyPhoneMutation
} from "../../../../redux/API/userSlice";
import {login} from "../../../../redux/isAuthSlice";
import { ReactComponent as ArrowIcon } from '../../../../assets/images/global-icons/arrow_ic.svg'

const BusinessPhoneVerify = () => {
  const savedForm = useSelector((state: RootState) => state.business);
  const [code, setCode] = useState(["", "", "", ""]);
  const inputRefs = useRef<(InputRef | null)[]>([]);
  const [timer, setTimer] = useState(60);
  const [canResend, setCanResend] = useState(false);
  const [error, setError] = useState(false);

  const [verifyCode] = useVerifyCodeMutation();
  const [verifyPhone] = useVerifyPhoneMutation();
  const [updateProfile] = useUpdateProfileMutation();
  const {data: userDate} = useGetMeQuery({accessToken: savedForm.accessToken})

  const isCodeComplete = code.every((digit) => digit !== "");

  const navigate = useNavigate();
  const dispatch = useDispatch();

  useEffect(() => {
    if (timer > 0) {
      const interval = setInterval(() => {
        setTimer((prev) => prev - 1);
      }, 1000);
      return () => clearInterval(interval);
    } else {
      setCanResend(true);
    }
  }, [timer]);

  const resendCode = async () => {
    setTimer(60);
    setCanResend(false);

    try {
      await verifyPhone({
        body: {
          countryCode: '1',
          phone: savedForm?.phone.replace(/\D/g, ""),
        },
        accessToken: savedForm.accessToken
      }).unwrap()
    } catch (error) {

    }
  };

  const setInputRef = (el: InputRef | null, index: number) => {
    if (el) inputRefs.current[index] = el;
  };

  const handleChange = (value: string, index: number) => {
    if (/^\d?$/.test(value)) {
      const newCode = [...code];
      newCode[index] = value;
      setCode(newCode);

      if (value && index < 3) {
        inputRefs.current[index + 1]?.focus();
      }
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>, index: number) => {
    if (e.key === "Backspace" && !code[index] && index > 0) {
      inputRefs.current[index - 1]?.focus();
    }
  };

  const handlePaste = (e: React.ClipboardEvent<HTMLInputElement>) => {
    e.preventDefault();
    const pastedText = e.clipboardData.getData("text").replace(/\D/g, "").slice(0, 4);

    if (pastedText.length > 0) {
      const newCode = [...code];
      pastedText.split("").forEach((char, index) => {
        newCode[index] = char;
      });

      setCode(newCode);
      const nextIndex = pastedText.length < 4 ? pastedText.length : 3;
      inputRefs.current[nextIndex]?.focus();
    }
  };

  const formatPhoneNumber = (phone: string): string => {
    const digits = phone.replace(/\D/g, "").slice(-10);
    if (digits.length < 3) return `+1 ${digits}`;
    if (digits.length < 6) return `+1 ${digits.slice(0, 3)} ${"*".repeat(digits.length - 3)}`;
    return `+1 ${digits.slice(0, 3)} ${"*".repeat(6)}${digits.slice(-2)}`;
  };

  const codeVerify = async () => {
    try {
      await verifyCode({
        body: {
          countryCode: '1',
          phone: savedForm.phone.replace(/\D/g, ""),
          otp: code.join(''),
        },
        accessToken: savedForm.accessToken
      }).unwrap();

      await updateProfile({
        body: {
          phone: `1 ${savedForm.phone.replace(/\D/g, '')}`,
        },
        id: userDate?.data.id,
        accessToken: savedForm.accessToken
      }).unwrap()

      dispatch(
        login({
          token: savedForm.accessToken
        })
      );

      navigate('/crm');
    } catch (e) {
      setError(true);
    }
  };

  return (
    <Flex className={styles.PhoneVerify__wrapper} justify="center" align="center">
      <ArrowIcon className={styles.PhoneVerify__back} onClick={() => navigate('../business/phone')}/>
      <Flex className={styles.PhoneVerify} justify="center" align="center" vertical gap={'60px'}>
        <Flex className={styles.PhoneVerify__header} vertical justify="center" align="center" gap="32px">
          <LogoIcon />
          <Flex vertical gap={'16px'}>
            <h1>Create New Account</h1>
            <p>Code has been sent to {formatPhoneNumber(savedForm.phone ?? '')}</p>
          </Flex>
        </Flex>
        <Flex vertical>
          <Flex gap="8px">
            {code.map((digit, index) => (
              <Input
                key={index}
                ref={(el) => setInputRef(el, index)}
                value={digit}
                maxLength={1}
                onChange={(e) => handleChange(e.target.value, index)}
                onKeyDown={(e) => handleKeyDown(e, index)}
                onPaste={index === 0 ? handlePaste : undefined}
                className={`${styles.PhoneVerify__input} ${digit ? styles.PhoneVerify__input_filled : ''} ${error ? styles.PhoneVerify__input_error : ''}`}
              />
            ))}
          </Flex>
          {error && <p className={styles.PhoneVerify__error}>Code is invalid. Give it another shot!</p>}
        </Flex>
        <Flex className={styles.PhoneVerify__resend} justify="center" align="center">
          {canResend ? (
            <Button type="link" onClick={resendCode} className={styles.PhoneVerify__resend__button}>
              Resend code
            </Button>
          ) : (
            <p>
              Resend code in <span style={{ color: "red" }}>{timer}</span> s
            </p>
          )}
        </Flex>
        <Flex className={styles.PhoneVerify__button}>
          <CustomButton text={'Verify'} onClick={codeVerify} disable={!isCodeComplete} />
        </Flex>
      </Flex>
    </Flex>
  );
};

export default BusinessPhoneVerify;
